import { useEffect, useState, useMemo } from 'react'
import {
  getAllNotesIdAsync,
  getNoteByIdAsync,
  clearNoteById,
  resetNotesListAndNoteById,
} from '../../../../../redux/flows/portal/currentPatient'
import { useAppDispatch, useAppSelector } from '../../../../../redux/store'
import { APIStatus } from '../../../../../redux/types'

const useNotes = () => {
  const dispatch = useAppDispatch()

  const currentPatient = useAppSelector((state) => state.currentPatientPortal.currentPatient)
  const notesIdList = useAppSelector((state) => state.currentPatientPortal.getAllNotesId)
  const notesIdListStatus = useAppSelector((state) => state.currentPatientPortal.getAllNotesIdStatus)

  const noteById = useAppSelector((state) => state.currentPatientPortal.noteById)
  const noteByIdStatus = useAppSelector((state) => state.currentPatientPortal.noteByIdStatus)

  const [currentIndex, setCurrentIndex] = useState(0)
  const [notes, setNotes] = useState({ s: '', o: '', a: '', p: '' })

  // -----------------------------------------------------
  // 1) Fetch all note IDs first (ALWAYS, no fallback)
  // -----------------------------------------------------
  useEffect(() => {
    if (!currentPatient?.patient?.id) return

    dispatch(clearNoteById()) // always clear first
    setCurrentIndex(0)

    dispatch(getAllNotesIdAsync({ patientId: currentPatient.patient.id }))
  }, [currentPatient?.patient?.id, dispatch])

  // -----------------------------------------------------
  // 2) When IDs arrive → fetch the newest note (index 0)
  // -----------------------------------------------------
  useEffect(() => {
    if (notesIdListStatus !== APIStatus.fulfilled) return
    if (!notesIdList || notesIdList.length === 0) return

    const firstNoteId = notesIdList[0]?.id
    if (firstNoteId) {
      dispatch(getNoteByIdAsync({ noteId: firstNoteId }))
    }
  }, [notesIdListStatus, notesIdList, dispatch])

  // -----------------------------------------------------
  // 3) The displayed note is ALWAYS the fetched noteById
  // No fallback to currentPatient.notes anymore
  // -----------------------------------------------------
  const displayedNote = useMemo(() => {
    return noteById
  }, [noteById])

  // -----------------------------------------------------
  // 4) Map displayedNote → notes fields
  // -----------------------------------------------------
  useEffect(() => {
    if (displayedNote) {
      setNotes({
        s: displayedNote.subjective ?? '',
        o: displayedNote.objective ?? '',
        a: displayedNote.assessment ?? '',
        p: displayedNote.plan ?? '',
      })
    }
  }, [displayedNote])

  // -----------------------------------------------------
  // 5) Pagination handlers (same behavior as scans)
  // -----------------------------------------------------

  // go to older note
  const handlePrevious = () => {
    if (currentIndex < notesIdList.length - 1) {
      const newIndex = currentIndex + 1
      const id = notesIdList[newIndex]?.id
      if (id) {
        setCurrentIndex(newIndex)
        dispatch(getNoteByIdAsync({ noteId: id }))
      }
    }
  }

  // go to newer note
  const handleNext = () => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1
      const id = notesIdList[newIndex]?.id
      if (id) {
        setCurrentIndex(newIndex)
        dispatch(getNoteByIdAsync({ noteId: id }))
      }
    }
  }

  const canGoPrevious = currentIndex < notesIdList.length - 1
  const canGoNext = currentIndex > 0

  const isLoading = notesIdListStatus === APIStatus.pending || noteByIdStatus === APIStatus.pending

  return {
    notes,
    displayedNote,
    handlePrevious,
    handleNext,
    canGoPrevious,
    canGoNext,
    isLoading,
    currentIndex,
    totalNotes: notesIdList.length,
  }
}

export default useNotes
